import { useState } from "react";
import { useAdminHomepageBanners, HomepageBanner, HomepageBannerFormData } from "@/hooks/useHomepageBanners";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface HomepageBannerFormProps {
  banner: HomepageBanner | null;
  onClose: () => void;
}

const iconOptions = [
  { value: "gift", label: "Gift" },
  { value: "sparkles", label: "Sparkles" },
  { value: "star", label: "Star" },
  { value: "zap", label: "Zap" },
  { value: "heart", label: "Heart" },
  { value: "calendar", label: "Calendar" },
  { value: "award", label: "Award" },
  { value: "ticket", label: "Ticket" },
];

const styleOptions = [
  { value: "primary", label: "Primary (Purple/Gold)" },
  { value: "secondary", label: "Secondary (Gold/Purple)" },
  { value: "success", label: "Success (Green)" },
  { value: "accent", label: "Accent" },
];

const HomepageBannerForm = ({ banner, onClose }: HomepageBannerFormProps) => {
  const { createBanner, updateBanner } = useAdminHomepageBanners();
  const isEditing = !!banner;

  const [formData, setFormData] = useState<HomepageBannerFormData>({
    name: banner?.name || "",
    slug: banner?.slug || "",
    title: banner?.title || "",
    subtitle: banner?.subtitle || "",
    description: banner?.description || "",
    icon: banner?.icon || "gift",
    cta_text: banner?.cta_text || "",
    cta_link: banner?.cta_link || "",
    badge_text: banner?.badge_text || "",
    is_active: banner?.is_active ?? false,
    display_order: banner?.display_order || 0,
    style_variant: banner?.style_variant || "primary",
    start_date: banner?.start_date
      ? format(new Date(banner.start_date), "yyyy-MM-dd'T'HH:mm")
      : "",
    end_date: banner?.end_date
      ? format(new Date(banner.end_date), "yyyy-MM-dd'T'HH:mm")
      : "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: HomepageBannerFormData = {
      ...formData,
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : undefined,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : undefined,
      subtitle: formData.subtitle || undefined,
      description: formData.description || undefined,
      cta_text: formData.cta_text || undefined,
      cta_link: formData.cta_link || undefined,
      badge_text: formData.badge_text || undefined,
    };

    if (isEditing) {
      await updateBanner.mutateAsync({ id: banner.id, data: submitData });
    } else {
      await createBanner.mutateAsync(submitData);
    }
    onClose();
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">
          {isEditing ? "Edit Banner" : "New Banner"}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Internal Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: !isEditing ? generateSlug(e.target.value) : formData.slug,
                });
              }}
              placeholder="New Year Campaign"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">URL Slug *</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: generateSlug(e.target.value) })
              }
              placeholder="new-year-campaign"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="title">Display Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="✨ New Year, New You ✨"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subtitle">Subtitle</Label>
            <Input
              id="subtitle"
              value={formData.subtitle}
              onChange={(e) =>
                setFormData({ ...formData, subtitle: e.target.value })
              }
              placeholder="Start 2025 with art"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Banner description (not shown on banner, for admin reference)"
            rows={2}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="icon">Icon</Label>
            <Select
              value={formData.icon}
              onValueChange={(value) =>
                setFormData({ ...formData, icon: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select icon" />
              </SelectTrigger>
              <SelectContent>
                {iconOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="style_variant">Style</Label>
            <Select
              value={formData.style_variant}
              onValueChange={(value) =>
                setFormData({ ...formData, style_variant: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select style" />
              </SelectTrigger>
              <SelectContent>
                {styleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) =>
                setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
              }
              min={0}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cta_text">CTA Button Text</Label>
            <Input
              id="cta_text"
              value={formData.cta_text}
              onChange={(e) =>
                setFormData({ ...formData, cta_text: e.target.value })
              }
              placeholder="Explore Offers"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cta_link">CTA Link</Label>
            <Input
              id="cta_link"
              value={formData.cta_link}
              onChange={(e) =>
                setFormData({ ...formData, cta_link: e.target.value })
              }
              placeholder="/offers or https://..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="badge_text">Badge Text</Label>
            <Input
              id="badge_text"
              value={formData.badge_text}
              onChange={(e) =>
                setFormData({ ...formData, badge_text: e.target.value })
              }
              placeholder="Limited Time"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date (Optional)</Label>
            <Input
              id="start_date"
              type="datetime-local"
              value={formData.start_date}
              onChange={(e) =>
                setFormData({ ...formData, start_date: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="end_date">End Date (Optional)</Label>
            <Input
              id="end_date"
              type="datetime-local"
              value={formData.end_date}
              onChange={(e) =>
                setFormData({ ...formData, end_date: e.target.value })
              }
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="is_active"
            checked={formData.is_active}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, is_active: checked })
            }
          />
          <Label htmlFor="is_active">Banner Active</Label>
        </div>

        <div className="flex gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createBanner.isPending || updateBanner.isPending}
          >
            {isEditing ? "Update Banner" : "Create Banner"}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default HomepageBannerForm;
